import isEmpty from "lodash/isEmpty";

import { splitUrl } from "@shared/utils";

import { UTM_PARAMS_SESSION_STORAGE_KEY } from "./constants";

export type TUtmParams = {
  utm_source: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
};

/**
 * A query string parsed into an object, e.g. next's `router.query`.
 */
type TQueryParams = Record<string, string | Array<string> | undefined>;

const defaultUtmParams: TUtmParams = {
  utm_source: "picter",
  utm_medium: undefined,
  utm_campaign: undefined,
  utm_term: undefined,
  utm_content: undefined,
};

const utmDefaultKeys: Array<string> = Object.keys(defaultUtmParams);

/**
 * Keep only the UTM parameters we know about, dropping the ones with no value.
 * A repeated param (`?utm_source=&utm_source=b`) collapses to its first usable value,
 * so every layer resolves the same URL the same way.
 */
export function pickUtmParams(params: TQueryParams = {}): Partial<TUtmParams> {
  return utmDefaultKeys.reduce((acc, key) => {
    const value = params[key];
    const firstUsable = (Array.isArray(value) ? value : [value]).find(
      (candidate) => !isEmpty(candidate),
    );
    return firstUsable === undefined ? acc : { ...acc, [key]: firstUsable };
  }, {});
}

/**
 * Store UTM parameters in session storage, if they are present in the query string.
 * This allows us to append them to links when the user navigates to another page
 * (e.g. the submission wizard). This is important for tracking the source of the user
 * across different pages on our side.
 */
export function storeUtmParams(queryString: string) {
  const searchParams = new URLSearchParams(queryString);
  // getAll, so a repeated param is collapsed by pickUtmParams exactly like router.query is
  const utmParams = pickUtmParams(
    Object.fromEntries(
      utmDefaultKeys.map((key) => [key, searchParams.getAll(key)]),
    ),
  );
  if (isEmpty(utmParams)) return;
  sessionStorage.setItem(
    UTM_PARAMS_SESSION_STORAGE_KEY,
    JSON.stringify(utmParams),
  );
}

/**
 * The UTM parameters an earlier page of this visit stored. Touches session storage, so
 * inside react it belongs in an effect, not in render — see the `useUtmParams` hook.
 */
export function getStoredUtmParams(): Partial<TUtmParams> {
  try {
    // Filtered like any other layer: storage can hold params from before these rules existed
    return pickUtmParams(
      JSON.parse(
        sessionStorage.getItem(UTM_PARAMS_SESSION_STORAGE_KEY) || "",
      ) || {},
    );
  } catch (error) {
    return {};
  }
}

/**
 * Merge the hard-coded UTM parameters with the ones of the current visit. Pure: the caller
 * decides where `visitParams` come from (the url, session storage, or both).
 *
 * They are merged per key, so a hard-coded parameter only fills in what the visit doesn't
 * carry.
 */
export function mergeUtmParams(
  defaults?: Partial<TUtmParams>,
  visitParams?: TQueryParams,
): TUtmParams {
  return {
    ...defaultUtmParams,
    ...defaults,
    ...pickUtmParams(visitParams),
  };
}

/**
 * `mergeUtmParams` for callers outside react, reading the stored params itself. Params from
 * `inboundParams` (the query params of the page we're on) win over the stored ones.
 */
export function getUtmParams(
  defaults?: Partial<TUtmParams>,
  inboundParams?: TQueryParams,
): TUtmParams {
  return mergeUtmParams(defaults, {
    ...getStoredUtmParams(),
    ...pickUtmParams(inboundParams),
  });
}

function decodeKey(pair: string) {
  const key = pair.split("=")[0];
  try {
    return decodeURIComponent(key);
  } catch (error) {
    // A malformed escape is nobody's key but its own literal self
    return key;
  }
}

/**
 * Add UTM parameters to a url, leaving the rest of it exactly as the destination declared it.
 *
 * The url's own query is carried over as text rather than parsed and re-serialised, because
 * re-serialising rewrites it: `URLSearchParams` emits form-urlencoding (a space becomes "+",
 * and `~!'()` get escaped), so a destination that percent-decodes its query — or verifies a
 * signature over the raw string — would see something other than what it published. Keeping
 * the text also keeps repeated parameters (`?cat=photo&cat=video`) and anything we have no
 * value for, since `params` carries a key for every utm parameter and most are usually
 * undefined.
 *
 * The parameters we do have values for replace the destination's, and go at the end.
 *
 * The fragment goes back after them, where it belongs: appended before, the parameters would be
 * buried inside the fragment and the destination would receive no query at all.
 */
export function appendUtmParams(url: string, params: TUtmParams) {
  const { baseUrl, query, hash } = splitUrl(url);

  const ours = Object.entries(params).filter(
    ([, value]) => value !== undefined && value !== null,
  );
  const replacedKeys = new Set(ours.map(([key]) => key));

  const search = [
    ...query
      .split("&")
      .filter(Boolean)
      .filter((pair) => !replacedKeys.has(decodeKey(pair))),
    ...ours.map(([key, value]) => `${key}=${encodeURIComponent(value)}`),
  ].join("&");

  // hash is null only when the url has no fragment; a bare trailing "#" is an empty one
  return `${baseUrl}?${search}${hash === null ? "" : `#${hash}`}`;
}
